import { supplierIdentityIds } from "./supplierIdentity";

const fields = ["invoice_number", "invoice_date", "currency", "subtotal", "tax", "total_amount"];
const present = (value) => value !== "" && value != null;
const referenceParts = (value) => String(value || "").normalize("NFKC").toUpperCase().match(/[\p{L}]+|\d+/gu) || [];
const referenceKey = (value) => referenceParts(value).join("|");
const formattedReferenceKey = (value) => referenceParts(value).map((part) => /^\d+$/.test(part) ? part.replace(/^0+(?=\d)/, "") : part).join("|");

// Display/manual selection only: keep number segments, and accept formatting
// differences only when there is one possible reference. Never guess a suffix.
export function invoiceReferenceForNote(references, note) {
  const key = referenceKey(note.delivery_note_number);
  if (!key) return null;
  const exact = references.filter((row) => referenceKey(row.reference) === key);
  if (exact.length) return exact.length === 1 ? exact[0] : null;
  const formatted = formattedReferenceKey(note.delivery_note_number);
  const matches = references.filter((row) => formattedReferenceKey(row.reference) === formatted);
  return matches.length === 1 ? matches[0] : null;
}
export const invoiceExtracted = (invoice) => {
  const source = invoice?.ocr_json?.extracted || {};
  const header = invoice?.ocr_json?.normalized_json?.header || {};
  return { ...header, supplier_name: header.supplier, ...source.header, ...source.totals, ...source };
};

export function createInvoiceLine(reference = "") {
  const id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return { id: `manual-${id}`, reference, net_amount: null, delivery_note_ids: [] };
}

function savedProposal(invoice) {
  const review = invoice?.applied_ai_review;
  return review?.review_id && review.review_id === invoice.applied_ai_review_id ? review : null;
}

function documentRows(source) {
  const rows = source?.invoice_lines?.length ? source.invoice_lines : source?.delivery_notes;
  return Array.isArray(rows) ? rows.filter(row => typeof row?.reference === "string" && row.reference.trim()) : [];
}

// A detected reference can describe a row before any delivery note is selected.
// Only project links that already belong to this invoice into the new display.
function projectRows(source, links, prefix) {
  const detected = documentRows(source);
  const rows = detected.map((row, index) => ({
    id: `${prefix}-${index + 1}`, reference: row.reference, net_amount: row.net_amount ?? null, delivery_note_ids: [],
  }));
  const references = detected.flatMap((row, index) => (row.kind === "order_summary"
    ? row.delivery_note_references || [] : [row.reference]).map(reference => ({ reference, index })));
  const numbers = links.map(link => formattedReferenceKey(link.delivery_note_number));
  const ambiguous = new Set(numbers.filter((number, index) => numbers.indexOf(number) !== index));
  for (const link of links) {
    const id = Number(link.delivery_note_id);
    const match = invoiceReferenceForNote(references, link);
    if (match && !ambiguous.has(formattedReferenceKey(link.delivery_note_number))) {
      rows[match.index].delivery_note_ids.push(id);
    } else {
      rows.push({ id: `note-${id}`, reference: link.delivery_note_number || String(id),
        net_amount: link.invoiced_amount ?? null, delivery_note_ids: [id] });
    }
  }
  const linksById = new Map(links.map(link => [Number(link.delivery_note_id), link]));
  rows.forEach((row, index) => {
    if (row.delivery_note_ids.length === 1 && detected[index]?.kind !== "order_summary") {
      const oldAmount = linksById.get(row.delivery_note_ids[0]).invoiced_amount;
      if (present(oldAmount)) row.net_amount = oldAmount;
    }
  });
  return rows;
}

function invoiceOcrRows(invoice) {
  return invoice?.ocr_json?.metadata?.multiple_invoices ? null : invoiceExtracted(invoice);
}

export function invoiceLinesForReview(invoice, proposal = null) {
  if (Array.isArray(invoice?.invoice_lines)) return invoice.invoice_lines;
  const ai = proposal || savedProposal(invoice);
  const useAi = documentRows(ai).length > 0;
  const source = useAi && !Array.isArray(ai.invoice_lines) ? { delivery_notes: invoiceReferenceReview(invoice, ai) } : ai;
  return projectRows(useAi ? source : invoiceOcrRows(invoice), invoice?.delivery_notes || [], useAi ? "ai" : "ocr");
}

function mergeDocumentRows(invoice, source, prefix, previousEvidence = invoice) {
  const current = invoiceLinesForReview(invoice);
  if (!documentRows(source).length) return current;
  if (!Array.isArray(invoice.invoice_lines)) return projectRows(source, invoice.delivery_notes || [], prefix);
  const baseline = invoiceLinesForReview({ ...previousEvidence, invoice_lines: null, delivery_notes: [] });
  const originalById = new Map(baseline.map(row => [row.id, row]));
  const comparableAmount = value => present(value) ? Number(value) : null;
  const editedAmount = row => {
    const original = originalById.get(row.id);
    return original ? comparableAmount(original.net_amount) !== comparableAmount(row.net_amount) : present(row.net_amount);
  };
  const untouched = row => !row.invoice_only && !row.delivery_note_ids.length && originalById.get(row.id)?.reference === row.reference && !editedAmount(row);
  const remaining = [...current], usedIds = new Set(current.map(row => row.id)), retained = new Set();
  const proposed = projectRows(source, [], prefix);
  const evidence = documentRows(source);
  const priorEvidence = documentRows(savedProposal(invoice) || invoiceOcrRows(invoice));
  const notes = new Map((invoice.delivery_notes || []).map(note => [Number(note.delivery_note_id), formattedReferenceKey(note.delivery_note_number)]));
  const incoming = proposed.flatMap((row, index) => {
    const key = referenceKey(row.reference);
    let position = remaining.findIndex(existing => referenceKey(existing.reference) === key);
    if (position === -1) position = remaining.findIndex(existing => referenceKey(originalById.get(existing.id)?.reference) === key);
    if (position === -1) {
      const formatted = formattedReferenceKey(row.reference);
      const same = current.filter(existing => formattedReferenceKey(existing.reference) === formatted);
      if (same.length === 1 && proposed.filter(other => formattedReferenceKey(other.reference) === formatted).length === 1) position = remaining.indexOf(same[0]);
    }
    // The AI may replace several printed delivery-note rows with their printed
    // order subtotal. Move their existing assignments once. If the user already
    // reviewed a conflicting breakdown, keep it instead of counting both forms.
    const members = new Set((evidence[index].delivery_note_references || []).map(formattedReferenceKey));
    if (members.size) {
      const covers = existing => {
        if (members.has(formattedReferenceKey(existing.reference))) return true;
        if (existing.delivery_note_ids.some(id => members.has(notes.get(Number(id))))) return true;
        const old = priorEvidence.find(entry => entry.reference === existing.reference);
        return old?.delivery_note_references?.some(ref => members.has(formattedReferenceKey(ref)));
      };
      const related = remaining.filter((existing, i) => i !== position && covers(existing));
      if (related.length) {
        const named = remaining[position];
        const group = named ? [named, ...related] : related;
        const isReviewed = existing => existing.invoice_only || existing.id.startsWith("manual-") || editedAmount(existing)
          || (originalById.has(existing.id) && existing.reference !== originalById.get(existing.id).reference)
          || (existing.delivery_note_ids.some(id => !members.has(notes.get(Number(id)))) && !members.has(formattedReferenceKey(existing.reference)));
        if (group.some(isReviewed)) {
          group.forEach(existing => retained.add(existing.id));
          return [];
        }
        for (const existing of group) remaining.splice(remaining.indexOf(existing), 1);
        return [{ ...row, id: group[0].id, delivery_note_ids: group.flatMap(existing => existing.delivery_note_ids) }];
      }
    }
    if (position !== -1) {
      const [existing] = remaining.splice(position, 1);
      const keepAmount = existing.invoice_only || editedAmount(existing) || (existing.delivery_note_ids.length && present(existing.net_amount));
      return { ...existing, net_amount: keepAmount ? existing.net_amount : row.net_amount ?? existing.net_amount };
    }
    if (usedIds.has(row.id)) row.id = createInvoiceLine().id;
    usedIds.add(row.id);
    return row;
  });
  return [...incoming, ...remaining.filter(row => retained.has(row.id) || !untouched(row))];
}

export function mergeInvoiceOcrLines(invoice, previousEvidence = invoice) {
  return mergeDocumentRows(invoice, invoiceOcrRows(invoice), "ocr", previousEvidence);
}

// An untouched scan opens with the same editable OCR defaults as a delivery note.
// Existing saved/manual values always win; matching notes requires explicit action.
export function fillInvoiceOcr(invoice, touched = new Set(), allowReviewed = false, freshOcr = false, previousEvidence = invoice) {
  if (!invoice || invoice.registered_at || invoice.status === "reconciled" || invoice.persisted_status === "reconciled") return invoice;
  const source = invoiceExtracted(invoice), patch = {};
  if (allowReviewed || !Number(invoice.revision)) {
    fields.forEach((key) => { if (!touched.has(key) && (!present(invoice[key]) || (key === "currency" && Number(invoice.revision) === 0 && !invoice.delivery_notes?.length)) && present(source[key])) patch[key] = source[key]; });
    const supplier = invoice.ocr_json?.suggested_supplier;
    if (!touched.has("supplier_id") && !invoice.supplier_id && supplier?.id) { patch.supplier_id = supplier.id; patch.supplier = supplier; }
  } else if (!freshOcr) return invoice;
  const applied = savedProposal(invoice);
  const mayFillLines = !invoice.applied_ai_review_id || Boolean(applied);
  const summaries = record => documentRows(invoiceOcrRows(record)).map(row => [row.kind || "delivery_note", row.reference, row.net_amount ?? null, row.delivery_note_references || []]);
  const refreshedRows = freshOcr && (previousEvidence === invoice || JSON.stringify(summaries(previousEvidence)) !== JSON.stringify(summaries(invoice)));
  if ((refreshedRows || !Array.isArray(invoice.invoice_lines)) && documentRows(invoiceOcrRows(invoice)).length && mayFillLines) {
    const merged = mergeInvoiceOcrLines(invoice, previousEvidence);
    if (documentRows(applied).length) {
      // A later catalogue lookup may supply amounts an applied AI review missed.
      // Its reviewed grouping and filled values remain the basis of these rows.
      const byId = new Map(merged.map(line => [line.id, line]));
      patch.invoice_lines = invoiceLinesForReview(previousEvidence).map(line => ({ ...line,
        net_amount: present(line.net_amount) ? line.net_amount : byId.get(line.id)?.net_amount ?? line.net_amount }));
    } else patch.invoice_lines = merged;
  }
  return Object.keys(patch).length ? { ...invoice, ...patch } : invoice;
}

// A match is a proposal until the user assigns it. Already reviewed assignments
// and invoice-only charges are excluded from the proposed additions.
export function invoiceLineMatches(invoice, source, lines = invoiceLinesForReview(invoice)) {
  if (!invoice?.supplier_id || !invoice.currency || !source) return new Map();
  const suppliers = new Set(supplierIdentityIds(invoice.supplier || invoice.supplier_id));
  const used = new Set(lines.flatMap(line => line.delivery_note_ids).map(Number));
  const rows = documentRows(source), references = source.delivery_notes || [];
  const matches = new Map();
  for (const line of lines) {
    if (line.invoice_only || line.delivery_note_ids.length) continue;
    const evidence = rows.filter(row => referenceKey(row.reference) === referenceKey(line.reference));
    if (evidence.length !== 1) continue;
    const wanted = evidence[0].kind === "order_summary" ? evidence[0].delivery_note_references || [] : [line.reference];
    const notes = wanted.flatMap(reference => {
      const found = references.filter(row => referenceKey(row.reference) === referenceKey(reference));
      const match = found.length === 1 && found[0].match_status === "matched" ? found[0].matched_delivery_note : null;
      if (!match || used.has(Number(match.id)) || match.currency !== invoice.currency ||
        !["verified", "verified_with_differences", "closed"].includes(match.status) ||
        ![match.supplier_id, match.canonical_supplier_id, ...(match.supplier_member_ids || [])].some(id => suppliers.has(Number(id)))) return [];
      return [match];
    });
    if (notes.length) matches.set(line.id, notes);
  }
  const counts = new Map();
  for (const notes of matches.values()) for (const note of notes) counts.set(Number(note.id), (counts.get(Number(note.id)) || 0) + 1);
  for (const [lineId, notes] of matches) {
    const unique = notes.filter(note => counts.get(Number(note.id)) === 1);
    if (unique.length) matches.set(lineId, unique); else matches.delete(lineId);
  }
  return matches;
}

export function assignInvoiceMatches(invoice, matches, lineId = null) {
  const links = [...(invoice.delivery_notes || [])];
  const lines = invoiceLinesForReview(invoice).map(line => {
    const notes = lineId && line.id !== lineId ? [] : matches.get(line.id) || [];
    if (!notes.length) return line;
    links.push(...notes.map(note => ({ delivery_note_id: Number(note.id), delivery_note_number: note.delivery_note_number,
      delivery_note_date: note.delivery_note_date, delivery_note_amount: note.total_amount,
      current_delivery_note_amount: note.total_amount, currency: note.currency, current_delivery_note_currency: note.currency,
      invoiced_amount: null, investigation_note: "", warnings: note.warnings || [] })));
    return { ...line, delivery_note_ids: notes.map(note => Number(note.id)) };
  });
  return { invoice_lines: lines, delivery_notes: links };
}

export function invoiceAiDraft(invoice) {
  return { supplier_id: invoice.supplier_id || null, supplier_name: invoice.supplier?.name || invoice.supplier_name || null,
    ...Object.fromEntries(fields.map((key) => [key, present(invoice[key]) ? (["subtotal", "tax", "total_amount"].includes(key) ? Number(invoice[key]) : invoice[key]) : null])), notes: invoice.notes || "",
    delivery_notes: (invoice.delivery_notes || []).map((row) => ({ delivery_note_id: row.delivery_note_id,
      reference: row.delivery_note_number, invoiced_amount: present(row.invoiced_amount) ? Number(row.invoiced_amount) : null,
      investigation_note: row.investigation_note || "" })) };
}

export function validateInvoiceProposal(value) {
  if (!value || typeof value !== "object" || !Array.isArray(value.delivery_notes) || !Array.isArray(value.warnings)) throw new Error("Invalid invoice proposal");
  return value;
}
export function applyInvoiceAiProposal(invoice, proposal) {
  const patch = {};
  fields.forEach((key) => { if (present(proposal[key])) patch[key] = proposal[key]; });
  const supplier = proposal.suggested_supplier;
  const changedSupplier = supplier?.id && Number(supplier.id) !== Number(invoice.supplier_id);
  if (supplier?.id) { patch.supplier_id = supplier.id; patch.supplier = supplier; }
  const lines = mergeDocumentRows(invoice, proposal, "ai");
  return { ...invoice, ...patch, invoice_lines: changedSupplier ? lines.map(line => ({ ...line, delivery_note_ids: [] })) : lines,
    delivery_notes: changedSupplier ? [] : invoice.delivery_notes || [] };
}

// A read reference and a linked delivery note are different things. Keep all
// detected references visible, even when no available note could be linked.
export function invoiceReferenceReview(invoice, appliedProposal = null) {
  const saved = invoice?.applied_ai_review;
  const proposal = appliedProposal || (saved?.review_id === invoice?.applied_ai_review_id ? saved : null);
  const detected = new Map();
  const ocr = invoice?.ocr_json?.metadata?.multiple_invoices ? [] : invoiceExtracted(invoice).delivery_notes || [];
  const proposed = proposal?.delivery_notes || [];
  [...ocr, ...proposed].forEach((entry) => {
    let key = referenceKey(entry.reference);
    if (!key) return;
    if (!detected.has(key) && proposed.includes(entry)) {
      const formatted = formattedReferenceKey(entry.reference);
      const same = [...detected.keys()].filter((candidate) => formattedReferenceKey(detected.get(candidate).reference) === formatted);
      if (same.length === 1 && proposed.filter((row) => formattedReferenceKey(row.reference) === formatted).length === 1) key = same[0];
    }
    detected.set(key, { ...detected.get(key), ...entry });
  });
  const links = invoice?.delivery_notes || [];
  const references = [...detected.values()];
  return references.map((entry) => ({ ...entry, linked: links.some((row) =>
    entry.matched_delivery_note?.id ? Number(row.delivery_note_id) === Number(entry.matched_delivery_note.id)
      : invoiceReferenceForNote(references, row) === entry && links.filter((link) => formattedReferenceKey(link.delivery_note_number) === formattedReferenceKey(row.delivery_note_number)).length === 1) }));
}
export function invoiceAiComparison(current, proposal) {
  const rows = [];
  const values = { supplier_name: proposal.suggested_supplier?.name || proposal.supplier_name,
    ...Object.fromEntries(fields.map((key) => [key, proposal[key]])) };
  const labels = { supplier_name: "Supplier", invoice_number: "Invoice number", invoice_date: "Invoice date", currency: "Currency", subtotal: "Net amount", tax: "Tax", total_amount: "Total" };
  Object.entries(values).forEach(([key, value]) => {
    if (present(value) && String(value) !== String(current[key] ?? "")) rows.push({ field: labels[key], before: current[key], after: value });
  });
  documentRows(proposal).forEach((entry) => {
    const evidence = entry.kind === "order_summary" ? null : invoiceReferenceForNote(proposal.delivery_notes, { delivery_note_number: entry.reference });
    const matched = evidence?.matched_delivery_note;
    const previous = current.delivery_notes?.find((note) => Number(note.delivery_note_id) === Number(matched?.id));
    const field = entry.kind === "order_summary" ? "Invoice line" : "Delivery note";
    rows.push({ field, before: previous?.reference, after: entry.reference, reason: matched?.delivery_note_number || evidence?.match_message || entry.reason,
      unmatched: field === "Delivery note" && !matched, amount: entry.net_amount });
  });
  return rows;
}
